<template>
  <main class="news-detail-page">
    <div v-if="!isLoaded && !article" class="news-detail-state" role="status" aria-live="polite">
      <span class="news-detail-spinner" aria-hidden="true"></span>
      <p>{{ t('news.loading') }}</p>
    </div>

    <div v-else-if="error" class="news-detail-state news-detail-state--message">
      <EmptyState
        icon="error"
        :title="t('news.loadingFailed')"
        :description="error"
        :show-action="true"
        :action-text="t('news.backToList')"
        @action="goBack"
      />
    </div>

    <article v-else-if="article" class="news-detail-article">
      <nav v-if="socialLinks.length" class="news-detail-socials" :aria-label="t('news.socialLinks')">
        <a
          v-for="social in socialLinks"
          :key="social.id"
          class="news-detail-social"
          :class="`news-detail-social--${social.id}`"
          :href="social.href"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="social.label"
        >
          <img :src="social.icon" alt="" aria-hidden="true">
        </a>
      </nav>

      <header class="news-detail-header">
        <h1>{{ article.title }}</h1>
        <p class="news-detail-meta">{{ formatDate(article.publishTime) }}</p>
      </header>

      <div class="news-detail-body">
        <div class="news-detail-richtext ql-editor" v-html="richTextContent"></div>
      </div>
    </article>

    <div v-else class="news-detail-state news-detail-state--message">
      <EmptyState
        icon="search"
        :title="t('news.notFound')"
        :description="t('news.notFoundDescription')"
        :show-action="true"
        :action-text="t('news.backToList')"
        @action="goBack"
      />
    </div>
  </main>
</template>

<script>
import EmptyState from '@/components/EmptyState.vue'
import { newsAPI } from '@/utils/api'

const {
  createNewsViewTracker,
  isLegacyNewsId,
  normalizeNewsDetailResponse,
  normalizeNewsRichText,
  parseNewsSocialLinks
} = require('@/utils/news-list.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')

const SOCIAL_ICONS = Object.freeze({
  facebook: '/static/images/newsroom-v2/detail-social/facebook.svg',
  instagram: '/static/images/newsroom-v2/detail-social/instagram.svg',
  linkedin: '/static/images/newsroom-v2/detail-social/linkedin.svg',
  youtube: '/static/images/newsroom-v2/detail-social/youtube.svg',
  tiktok: '/static/images/newsroom-v2/detail-social/tiktok.svg',
  x: '/static/images/newsroom-v2/detail-social/x.svg'
})

export default {
  name: 'NewsDetail',
  components: {
    EmptyState
  },
  mixins: [siteLanguageMixin],
  data() {
    return {
      article: null,
      error: null,
      isLoaded: false,
      shouldIncrementView: createNewsViewTracker(),
      detailRequestGuard: createLatestSiteRequestGuard()
    }
  },
  computed: {
    socialLinks() {
      if (!this.article) return []
      return parseNewsSocialLinks(this.article.socialLinksJson).map((social) => ({
        ...social,
        icon: SOCIAL_ICONS[social.id]
      }))
    },
    richTextContent() {
      return normalizeNewsRichText(this.article && this.article.content)
    }
  },
  async mounted() {
    await this.fetchNewsDetail()
  },
  watch: {
    '$route.params.urlSlug'(urlSlug, previousUrlSlug) {
      if (urlSlug !== previousUrlSlug) this.fetchNewsDetail()
    },
    currentLanguage() {
      this.$router.replace('/news')
    }
  },
  methods: {
    async fetchNewsDetail() {
      const request = this.detailRequestGuard.start(this.currentLanguage)
      this.error = null
      this.isLoaded = false
      this.article = null

      const urlSlug = this.$route.params.urlSlug
      if (!urlSlug) {
        this.error = this.t('news.invalidUrl')
        this.isLoaded = true
        return
      }

      try {
        const article = normalizeNewsDetailResponse(await newsAPI.getNewsDetail(urlSlug, request.siteType))
        if (!this.detailRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.article = article
        document.title = `${this.article.title} - ANOVATOR`
        this.incrementViewOnce(urlSlug, request.siteType)
      } catch (error) {
        if (!this.detailRequestGuard.isCurrent(request, this.currentLanguage)) return
        if (isLegacyNewsId(urlSlug) && await this.redirectLegacyNewsId(urlSlug, request)) return
        console.error('获取新闻详情失败:', error)
        this.error = this.t('news.detailNetworkError')
      } finally {
        if (this.detailRequestGuard.isCurrent(request, this.currentLanguage)) this.isLoaded = true
      }
    },

    async redirectLegacyNewsId(id, request) {
      try {
        const legacyArticle = normalizeNewsDetailResponse(await newsAPI.getNewsDetailById(id, request.siteType))
        if (!this.detailRequestGuard.isCurrent(request, this.currentLanguage)) return false
        if (!legacyArticle.urlSlug) return false
        await this.$router.replace({ name: 'NewsDetail', params: { urlSlug: legacyArticle.urlSlug } })
        return true
      } catch (error) {
        return false
      }
    },

    incrementViewOnce(urlSlug, siteType) {
      if (!this.shouldIncrementView(urlSlug)) return
      newsAPI.increaseViewCount(urlSlug, siteType).catch(error => {
        console.error('增加浏览量失败:', error)
      })
    },

    formatDate(value) {
      if (!value) return ''
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(this.currentLanguage === 'ZH' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    goBack() {
      this.$router.push('/news')
    }
  }
}
</script>

<style scoped>
@import '../assets/css/quill.snow.css';

.news-detail-page,
.news-detail-page * {
  box-sizing: border-box;
}

.news-detail-page {
  min-width: 0;
  min-height: 100vh;
  padding-top: var(--global-header-height, 44px);
  overflow: hidden;
  color: #000;
  background: #fff;
  font-family: MiSans, 'MiSans-Regular', Arial, sans-serif;
}

.news-detail-article {
  width: min(calc(100vw - 144px), 1296px);
  margin-top: 0;
  margin-right: auto;
  margin-bottom: 0;
  margin-left: max(72px, calc((100vw - 1296px) / 2));
  padding: 80px 0 160px;
}

.news-detail-socials {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  margin: 0 0 20px;
}

.news-detail-social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border: 1px solid #fff;
  border-radius: 50%;
  color: #000;
  background: #f5f5f7;
  text-decoration: none;
  transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.news-detail-social img {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.news-detail-social--facebook img {
  width: 14px;
  height: 25px;
}

.news-detail-social--youtube img {
  width: 23px;
  height: 16px;
}

.news-detail-social--linkedin img {
  width: 22px;
  height: 21px;
}

.news-detail-social--x img {
  width: 15px;
  height: 15px;
}

.news-detail-social:hover,
.news-detail-social:focus-visible {
  background: rgba(239, 10, 106, 0.12);
  transform: translateY(-2px);
}

.news-detail-social:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 3px;
}

.news-detail-header h1 {
  max-width: 1296px;
  margin: 0;
  color: #000;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.34;
  letter-spacing: -0.02em;
  overflow-wrap: anywhere;
  text-align: left;
}

.news-detail-meta {
  margin: 12px 0 0;
  color: rgba(0, 0, 0, 0.5);
  font-size: 12px;
  line-height: 1.35;
  text-align: left;
}

.news-detail-body {
  margin-top: 40px;
  color: rgba(0, 0, 0, 0.8);
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: anywhere;
  text-align: left;
}

.news-detail-summary {
  margin: 0 0 26px;
  color: rgba(0, 0, 0, 0.8);
  font-size: inherit !important;
  line-height: inherit !important;
  text-align: left;
}

.news-detail-richtext.ql-editor {
  min-height: 0;
  padding: 0;
  overflow: visible;
  border: 0;
  color: inherit;
  background: transparent;
  font: inherit;
  line-height: inherit;
  text-align: left;
  white-space: normal;
}

.news-detail-richtext :deep(p),
.news-detail-richtext :deep(ul),
.news-detail-richtext :deep(ol),
.news-detail-richtext :deep(blockquote),
.news-detail-richtext :deep(pre),
.news-detail-richtext :deep(table) {
  margin-top: 0;
  margin-bottom: 24px;
  text-align: left;
}

.news-detail-richtext :deep(p) {
  color: inherit;
  font-size: inherit !important;
  line-height: inherit !important;
}

.news-detail-richtext :deep(h1),
.news-detail-richtext :deep(h2),
.news-detail-richtext :deep(h3),
.news-detail-richtext :deep(h4),
.news-detail-richtext :deep(h5),
.news-detail-richtext :deep(h6) {
  margin: 32px 0 16px;
  color: #000;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
}

.news-detail-richtext :deep(h1) { font-size: 28px; }
.news-detail-richtext :deep(h2) { font-size: 24px; }
.news-detail-richtext :deep(h3) { font-size: 20px; }
.news-detail-richtext :deep(h4),
.news-detail-richtext :deep(h5),
.news-detail-richtext :deep(h6) { font-size: 16px; }

.news-detail-richtext :deep(ul),
.news-detail-richtext :deep(ol) {
  padding-left: 24px;
}

.news-detail-richtext :deep(li) {
  margin: 6px 0;
}

.news-detail-richtext :deep(a) {
  color: #ef0a6a;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.news-detail-richtext :deep(blockquote) {
  padding: 18px 22px;
  border-left: 4px solid #ef0a6a;
  color: rgba(0, 0, 0, 0.72);
  background: #f5f5f7;
}

.news-detail-richtext :deep(pre) {
  max-width: 100%;
  padding: 18px;
  overflow-x: auto;
  border-radius: 10px;
  color: #fff;
  background: #151515;
  white-space: pre;
}

.news-detail-richtext :deep(code) {
  font-family: Consolas, 'Courier New', monospace;
}

.news-detail-richtext :deep(table) {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
}

.news-detail-richtext :deep(th),
.news-detail-richtext :deep(td) {
  min-width: 120px;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.16);
  text-align: left;
}

.news-detail-richtext :deep(img) {
  display: block;
  width: 100% !important;
  max-width: 100%;
  height: auto !important;
  margin: 30px 0;
  border-radius: 24px;
  object-fit: cover;
}

.news-detail-richtext :deep(p:has(> img:only-child)) {
  margin: 30px 0;
}

.news-detail-richtext :deep(p:has(> img:only-child) > img) {
  margin: 0;
}

.news-detail-richtext :deep(p:has(> img:nth-of-type(2))) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0;
  margin: 30px 0;
  overflow: hidden;
  border-radius: 24px;
}

.news-detail-richtext :deep(p:has(> img:nth-of-type(2)) > img) {
  height: 337px !important;
  margin: 0;
  border-radius: 0;
}

.news-detail-richtext :deep(iframe),
.news-detail-richtext :deep(video) {
  display: block;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  margin: 30px 0;
  border: 0;
  border-radius: 24px;
}

.news-detail-richtext :deep(*:last-child) {
  margin-bottom: 0;
}

.news-detail-state {
  display: flex;
  min-height: calc(100vh - var(--global-header-height, 44px));
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 80px 16px;
  color: #000;
  background: #fff;
}

.news-detail-state p {
  margin: 18px 0 0;
  color: rgba(0, 0, 0, 0.65);
}

.news-detail-spinner {
  width: 34px;
  height: 34px;
  border: 2px solid rgba(0, 0, 0, 0.16);
  border-top-color: #ef0a6a;
  border-radius: 50%;
  animation: news-detail-spin 0.8s linear infinite;
}

@keyframes news-detail-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1023px) {
  .news-detail-article {
    width: calc(100vw - 48px);
    margin-left: 24px;
  }
}

@media (max-width: 700px) {
  .news-detail-page {
    padding-top: var(--global-header-height, 50px);
  }

  .news-detail-article {
    width: calc(100vw - 32px);
    margin-left: 16px;
    padding: 60px 0 72px;
  }

  .news-detail-socials {
    gap: 10px;
    margin-bottom: 20px;
  }

  .news-detail-social {
    width: 48px;
    height: 48px;
    flex-basis: 48px;
  }

  .news-detail-social img {
    max-width: 24px;
    max-height: 25px;
  }

  .news-detail-header h1 {
    font-size: 30px;
    line-height: 1.33;
  }

  .news-detail-meta {
    margin-top: 12px;
    font-size: 12px;
  }

  .news-detail-body {
    margin-top: 30px;
    line-height: 1.45;
  }

  .news-detail-summary,
  .news-detail-richtext :deep(p),
  .news-detail-richtext :deep(ul),
  .news-detail-richtext :deep(ol),
  .news-detail-richtext :deep(blockquote),
  .news-detail-richtext :deep(pre),
  .news-detail-richtext :deep(table) {
    margin-bottom: 20px;
  }

  .news-detail-richtext :deep(h1) { font-size: 24px; }
  .news-detail-richtext :deep(h2) { font-size: 22px; }
  .news-detail-richtext :deep(h3) { font-size: 19px; }

  .news-detail-richtext :deep(img),
  .news-detail-richtext :deep(iframe),
  .news-detail-richtext :deep(video),
  .news-detail-richtext :deep(p:has(> img:only-child)),
  .news-detail-richtext :deep(p:has(> img:nth-of-type(2))) {
    margin: 24px 0;
    border-radius: 10px;
  }

  .news-detail-richtext :deep(p:has(> img:nth-of-type(2))) {
    grid-template-columns: 1fr;
    gap: 16px;
    overflow: visible;
  }

  .news-detail-richtext :deep(p:has(> img:nth-of-type(2)) > img) {
    height: auto !important;
    border-radius: 10px;
  }

  .news-detail-state {
    min-height: calc(100vh - var(--global-header-height, 50px));
  }
}

@media (prefers-reduced-motion: reduce) {
  .news-detail-social {
    transition: none;
  }

  .news-detail-spinner {
    animation-duration: 1.6s;
  }
}
</style>
